import{lazy,Suspense}from'react';
import{Navigate,Route,Routes}from'react-router-dom';
import{Alert,Box,CircularProgress}from'@mui/material';
import{getSession}from'./api';
import AppShell from'./AppShell';

const Login=lazy(()=>import('./pages/LoginPage'));
const TrialRegistration=lazy(()=>import('./pages/TrialRegistrationPage'));
const Overview=lazy(()=>import('./pages/OverviewPage'));
const Projects=lazy(()=>import('./pages/ProjectsPage'));
const SmartProject=lazy(()=>import('./pages/SmartProjectPage'));
const ProjectOperations=lazy(()=>import('./pages/ProjectPage'));
const Workstreams=lazy(()=>import('./pages/ProjectWorkstreamsPage'));
const ValueStreams=lazy(()=>import('./pages/ValueStreamsPage'));
const Attention=lazy(()=>import('./pages/AttentionPage'));
const GciLibrary=lazy(()=>import('./pages/GciLibraryPage'));
const Resources=lazy(()=>import('./pages/ResourcesPage'));
const HumanResources=lazy(()=>import('./pages/HumanResourcesPage'));
const HrEmployeeProfile=lazy(()=>import('./pages/HrEmployeeProfilePage'));
const WorkforceIntelligence=lazy(()=>import('./pages/WorkforceIntelligencePage'));

const DepartmentResources=lazy(()=>import('./pages/DepartmentResourcesPage'));
const OrganisationResourceRegister=lazy(()=>import('./pages/OrganisationResourceRegisterPage'));
const Rosters=lazy(()=>import('./pages/RosterPage'));

const CRM=lazy(()=>import('./pages/CrmPage'));
const SalesPipeline=lazy(()=>import('./pages/SalesPipelinePage'));
const Finance=lazy(()=>import('./pages/FinancePage'));
const Billing=lazy(()=>import('./pages/BillingPage'));
const Accounting=lazy(()=>import('./pages/AccountingPage'));
const Executive=lazy(()=>import('./pages/ExecutivePage'));
const SteerCo=lazy(()=>import('./pages/SteerCoPage'));
const Roadmap=lazy(()=>import('./pages/RoadmapPage'));
const Platform=lazy(()=>import('./pages/PlatformPage'));
const PlatformCommercial=lazy(()=>import('./pages/PlatformCommercialPage'));
const PlatformDataModel=lazy(()=>import('./pages/PlatformDataModelPage'));
const OrganisationFields=lazy(()=>import('./pages/OrganisationFieldsPage'));
const PlatformOnboarding=lazy(()=>import('./pages/PlatformOnboardingPage'));
const PlatformBlueprints=lazy(()=>import('./pages/PlatformBlueprintsPage'));
const PlatformOrganisationProfile=lazy(()=>import('./pages/PlatformOrganisationProfilePage'));

const AdminLayout=lazy(()=>import('./pages/AdminLayout'));
const AdminOverview=lazy(()=>import('./pages/AdminOverviewPage'));
const AdminRules=lazy(()=>import('./pages/AdminProjectRulesPage'));
const AdminProjectManagement=lazy(()=>import('./pages/AdminProjectManagementPage'));
const AdminResource=lazy(()=>import('./pages/AdminResourcePage'));
const AdminCommercial=lazy(()=>import('./pages/AdminCommercialPage'));
const AdminReporting=lazy(()=>import('./pages/AdminReportingPage'));
const AdminPilotReadiness=lazy(()=>import('./pages/AdminPilotReadinessPage'));
const ProjectTemplates=lazy(()=>import('./pages/ProjectTemplatesPage'));
const Integration=lazy(()=>import('./pages/IntegrationPage'));
const WorkbookPlans=lazy(()=>import('./pages/WorkbookPlansPage'));
const Organisation=lazy(()=>import('./pages/OrganisationPage'));
const DepartmentModules=lazy(()=>import('./pages/DepartmentModulesPage'));
const Access=lazy(()=>import('./pages/AccessPage'));
const Audit=lazy(()=>import('./pages/AuditPage'));
const UserManual=lazy(()=>import('./pages/UserManualPage'));

const Loading=()=>
 <Box display="grid" minHeight="50vh" sx={{placeItems:'center'}}>
  <CircularProgress/>
 </Box>;

function Gate(){
 return getSession()
  ?<AppShell/>
  :<Navigate to="/login" replace/>;
}

function Home(){
 const s=getSession();
 if(s?.kind==='platform')return <Navigate to="/platform" replace/>;
 // A Department should land in its own workspace, not a portfolio dashboard
 // that queries Reporting even when Reporting is restricted.
 if(s?.moduleRestricted){
  if(s.businessModules?.includes('reporting')&&s.permissions.includes('reporting.view'))
   return <Overview/>;
  const choices=[
   ['projects','/projects','project.view'],['finance','/finance','finance.view'],
   ['billing','/billing','billing.view'],['accounting','/billing/accounting','billing.view'],
   ['governance','/steerco','reporting.view'],['resources','/resources','resource.view'],
   ['hr','/hr','resource.view'],['sales','/sales','sales.view'],['crm','/crm','crm.view'],
   ['administration','/admin','organisation.manage']
  ];
  const route=choices.find(([module,,permission])=>
   s.businessModules?.includes(module)&&s.permissions.includes(permission));
  if(route)return <Navigate to={route[1]} replace/>;
  return <Box p={3}><Alert severity="info">
   No operational modules are assigned to this account. Contact your Organisation Administrator.
  </Alert></Box>;
 }
 return <Overview/>;
}

export default function App(){
 return <Suspense fallback={<Loading/>}>
  <Routes>
   <Route path="/login" element={<Login/>}/>
   <Route path="/trial" element={<TrialRegistration/>}/>
   <Route element={<Gate/>}>
    <Route index element={<Home/>}/>
    <Route path="projects" element={<Projects/>}/>
    <Route path="projects/:id" element={<SmartProject/>}/>
    <Route path="projects/:id/operations" element={<ProjectOperations/>}/>
    <Route path="projects/:id/workstreams" element={<Workstreams/>}/>
    <Route path="value-streams" element={<ValueStreams/>}/>
    <Route path="attention" element={<Attention/>}/>
    <Route path="gci" element={<GciLibrary/>}/>
    <Route path="resources" element={<Resources/>}/>
    <Route path="hr" element={<HumanResources/>}/>
    <Route path="hr/employees" element={<OrganisationResourceRegister/>}/>
    <Route path="hr/employees/:id" element={<HrEmployeeProfile/>}/>
    <Route path="hr/import" element={<Integration/>}/>
    <Route path="hr/policies" element={<AdminResource/>}/>
    <Route path="workforce-intelligence" element={<WorkforceIntelligence/>}/>
    <Route path="resources/departments" element={<DepartmentResources/>}/>
    <Route path="resources/roster" element={<Rosters/>}/>
    <Route path="crm" element={<CRM/>}/>
    <Route path="sales" element={<SalesPipeline/>}/>
    <Route path="finance" element={<Finance/>}/>
    <Route path="billing" element={<Billing/>}/>
    <Route path="billing/accounting" element={<Accounting/>}/>
    <Route path="executive" element={<Executive/>}/>
    <Route path="steerco" element={<SteerCo/>}/>
    <Route path="roadmap" element={<Roadmap/>}/>
    <Route path="manual" element={<UserManual area="organisation"/>}/>

    <Route path="admin" element={<AdminLayout/>}>
     <Route index element={<AdminOverview/>}/>
     <Route path="organisation" element={<Organisation/>}/>
     <Route path="project-templates" element={<ProjectTemplates/>}/>
     <Route path="project-management" element={<AdminProjectManagement/>}/>
     <Route path="project-intelligence" element={<AdminRules/>}/>
     <Route path="resources" element={<AdminResource/>}/>
     <Route path="resource-register" element={<OrganisationResourceRegister/>}/>
     <Route path="commercial" element={<AdminCommercial/>}/>
     <Route path="reporting" element={<AdminReporting/>}/>
     <Route path="pilot-readiness" element={<AdminPilotReadiness/>}/>
     <Route path="project-rules" element={<Navigate to="/admin/project-intelligence" replace/>}/>
     <Route path="data-integration" element={<Integration/>}/>
      <Route path="fields" element={<OrganisationFields/>}/>
     <Route path="workbook-plans" element={<WorkbookPlans/>}/>
     <Route path="access" element={<Access/>}/>
     <Route path="module-access" element={<DepartmentModules/>}/>
     <Route path="audit" element={<Audit/>}/>
     <Route path="manual" element={<UserManual area="organisation"/>}/>
    </Route>

    <Route path="project-templates" element={<Navigate to="/admin/project-templates" replace/>}/>
    <Route path="integration" element={<Navigate to="/admin/data-integration" replace/>}/>
    <Route path="organisation" element={<Navigate to="/admin/organisation" replace/>}/>
    <Route path="access" element={<Navigate to="/admin/access" replace/>}/>
    <Route path="audit" element={<Navigate to="/admin/audit" replace/>}/>

    <Route path="platform" element={<Platform/>}/>
    <Route path="platform/templates" element={<Platform/>}/>
    <Route path="platform/activity" element={<Platform/>}/>
    <Route path="platform/manual" element={<UserManual area="platform"/>}/>
    <Route path="platform/subscriptions" element={<PlatformCommercial/>}/>
     <Route path="platform/data-model" element={<PlatformDataModel/>}/>
    <Route path="platform/organisations/:id/onboarding" element={<PlatformOnboarding/>}/>
     <Route path="platform/organisations/:id/blueprints" element={<PlatformBlueprints/>}/>
     <Route path="platform/organisations/:id/profile" element={<PlatformOrganisationProfile/>}/>
   </Route>
   <Route path="*" element={<Navigate to="/" replace/>}/>
  </Routes>
 </Suspense>;
}
